import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { MessagePopupService } from '../../shared/components/message-popup/message-popup.service';
import { AuthService } from '../services/auth.service';

//Marker set on an error the API refused with 429. The popup for it is shown here, once, so a
//caller only needs to know that the user has already been told - see isRateLimited() - and must
//not add a message of its own. Without that check a 429 on a seat reservation would answer with
//"locul a fost rezervat deja de altcineva", which is not what happened.
const RATE_LIMITED = '_softransRateLimited';

//Used when the API answers 429 with an empty or non-textual body. The API normally sends its own
//Romanian message including the delay; Retry-After cannot be read here because the API exposes
//only Content-Range through Access-Control-Expose-Headers.
const RATE_LIMIT_MESSAGE = 'Ați făcut prea multe cereri într-un interval scurt. '
  + 'Vă rugăm să încercați din nou mai târziu.';

//Only one rate-limit popup at a time: several requests in flight can come back 429 together and
//each dialog would otherwise stack on top of the previous one. Held here rather than in a service
//so that whenRateLimitPopupClosed() can be awaited by a caller that is about to reload the page -
//a reload while this dialog is open would take the explanation away with it.
let rateLimitPopup: Promise<unknown> | null = null;

export function isRateLimited(error: unknown): boolean {
  return !!error && (error as any)[RATE_LIMITED] === true;
}

/** Resolves once the user has closed the rate-limit popup, or immediately when none is open. */
export function whenRateLimitPopupClosed(): Promise<unknown> {
  return rateLimitPopup ?? Promise.resolve();
}

export const errorCatchingInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const messagePopupService = inject(MessagePopupService);

  return next(req).pipe(
    catchError(err => {
      console.error("errorCatchingInterceptor", err);
      if (err.status === 428) {
        messagePopupService.showMessage("Sesiunea a expirat, veti fi redirectionat catre pagina de login.", 'error');
        auth.logout();
      }
      if (err.status === 403 && auth.isLoggedIn()) {
        messagePopupService.showMessage(err.error, 'error');
        auth.logout();
      }
      if (err.status === 429) {
        err[RATE_LIMITED] = true;
        if (rateLimitPopup === null) {
          //the API sends its own Romanian message, including how long the caller has to wait
          const message = typeof err.error === 'string' && err.error.trim() !== ''
            ? err.error
            : RATE_LIMIT_MESSAGE;
          rateLimitPopup = messagePopupService.showMessage(message, 'warning')
            .finally(() => rateLimitPopup = null);
        }
      }
      return throwError(() => err);
    })
  );
};
